import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log('=== Supabase Config Debug ===');
console.log('VITE_SUPABASE_URL:', supabaseUrl ? '✅ Loaded' : '❌ Missing');
console.log('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✅ Loaded' : '❌ Missing');

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables!');
  console.error('Make sure .env file exists in root directory with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Profile = {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  role: 'admin' | 'director' | 'manager' | 'remote_ops' | 'maintenance' | 'inspector' | 'cleaner' | 'owner' | 'accounting';
  is_active: boolean;
  last_active?: string;
  session_started_at?: string;
  created_at: string;
  updated_at: string;
};

// Session management
const SESSION_MAX_AGE = 12 * 60 * 60 * 1000;
const INACTIVE_TIMEOUT = 2 * 60 * 60 * 1000;

let inactivityTimer: number | null = null;
let sessionTimer: number | null = null;

export function trackActivity() {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(async () => {
    console.log('User inactive for 2 hours, logging out...');
    await forceLogout();
  }, INACTIVE_TIMEOUT);
  updateLastActive();
}

async function updateLastActive() {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user) {
    try {
      await supabase
        .from('profiles')
        .update({ last_active: new Date().toISOString() })
        .eq('id', session.user.id);
    } catch (err) {
      // Ignore column errors
    }
  }
}

export async function forceLogout() {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  if (sessionTimer) clearTimeout(sessionTimer);
  await supabase.auth.signOut();
  sessionStorage.removeItem('userProfile');
  sessionStorage.removeItem('userRole');
  sessionStorage.removeItem('fieldViewRole');   // R64: a switched field view must never leak to the next login on this tab
  sessionStorage.removeItem('userBaseRole');
  window.location.href = '/login?session_expired=true';
}

export async function checkSessionValidity(): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return false;
  
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', session.user.id)
    .single();
  
  if (error || !profile) return true;
  
  const now = Date.now();
  const sessionStart = profile.session_started_at ? new Date(profile.session_started_at).getTime() : now;
  const lastActive = profile.last_active ? new Date(profile.last_active).getTime() : now;
  
  if (now - sessionStart > SESSION_MAX_AGE) {
    await forceLogout();
    return false;
  }
  
  if (now - lastActive > INACTIVE_TIMEOUT) {
    await forceLogout();
    return false;
  }
  
  const timeUntilSessionEnd = SESSION_MAX_AGE - (now - sessionStart);
  if (sessionTimer) clearTimeout(sessionTimer);
  sessionTimer = setTimeout(async () => { await forceLogout(); }, timeUntilSessionEnd);
  
  trackActivity();
  return true;
}

export function initSessionTracking() {
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  events.forEach(event => { document.addEventListener(event, trackActivity); });
  trackActivity();
}

export async function updateSessionStart() {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user) {
    const now = new Date().toISOString();
    try {
      await supabase
        .from('profiles')
        .update({ session_started_at: now, last_active: now })
        .eq('id', session.user.id);
    } catch (err) {
      // Ignore column errors
    }
  }
}
